/**
 * Colour swatches — goods-flow spec §9, item 8.10: *"colour swatches beside colour
 * options"*. The atelier called it a detail; it is also the cheapest way to make a list
 * of forty near-synonyms scannable.
 *
 * GUARDRAIL 9 IS THE WHOLE DESIGN HERE. The colour field is FREE ENTRY, and the
 * walkthrough already recorded that the brand list holds the same value under several
 * typo variants — the colour list is no better. So a function that had to produce a
 * colour for any string would be inventing data, and a swatch is a particularly bad
 * place to invent: a wrong square does not read as a guess, it reads as a fact about a
 * garment.
 *
 * THEREFORE: an unknown value gets NO SWATCH AT ALL, not a grey or transparent one.
 * Grey would be indistinguishable from the answer for "grijs", and an empty outline
 * would still occupy the slot where a fact goes. Absence is the honest rendering of "we
 * do not know what colour this word means", and it costs the user nothing — the word
 * itself is still right there.
 *
 * NO FUZZY MATCHING, no prefix matching, no "contains blue therefore blue". *Donkerblauw*
 * and *lichtblauw* are genuinely different colours and both contain *blauw*; guessing
 * from a substring would paint them the same. Compound words are listed explicitly or
 * they are unknown.
 *
 * The two languages are both here because the atelier types in Dutch and the interface
 * ships in English (D-GF10) — the same garment can be entered either way, and neither
 * spelling is more correct than the other.
 */

/**
 * Hex is deliberately a plain conventional rendering, not a colour-science conversion.
 * These are labels for a rack, not a print profile.
 */
const KNOWN: Readonly<Record<string, string>> = {
  // Neutrals
  black: '#000000',
  zwart: '#000000',
  white: '#ffffff',
  wit: '#ffffff',
  grey: '#808080',
  gray: '#808080',
  grijs: '#808080',
  'light grey': '#c8c8c8',
  'lichtgrijs': '#c8c8c8',
  'dark grey': '#4a4a4a',
  'donkergrijs': '#4a4a4a',
  cream: '#fffdd0',
  crème: '#fffdd0',
  creme: '#fffdd0',
  beige: '#f5f5dc',
  ecru: '#f0e6d2',
  taupe: '#8b7d6b',
  // Primaries and the rest of the wheel
  red: '#d32f2f',
  rood: '#d32f2f',
  'dark red': '#8b1a1a',
  'donkerrood': '#8b1a1a',
  burgundy: '#6d1a2e',
  bordeaux: '#6d1a2e',
  orange: '#ef6c00',
  oranje: '#ef6c00',
  yellow: '#fbc02d',
  geel: '#fbc02d',
  mustard: '#c9a227',
  okergeel: '#c9a227',
  green: '#2e7d32',
  groen: '#2e7d32',
  'dark green': '#1b4d24',
  'donkergroen': '#1b4d24',
  'light green': '#7cb342',
  'lichtgroen': '#7cb342',
  olive: '#6b7031',
  olijf: '#6b7031',
  khaki: '#8f8054',
  blue: '#1565c0',
  blauw: '#1565c0',
  'light blue': '#64b5f6',
  'lichtblauw': '#64b5f6',
  'dark blue': '#0d326b',
  'donkerblauw': '#0d326b',
  navy: '#101f45',
  marine: '#101f45',
  marineblauw: '#101f45',
  purple: '#6a1b9a',
  paars: '#6a1b9a',
  lilac: '#b39ddb',
  lila: '#b39ddb',
  pink: '#ec407a',
  roze: '#ec407a',
  brown: '#5d4037',
  bruin: '#5d4037',
  camel: '#c19a6b',
  // Metallics: a flat hex is a convention for these rather than a description. Listed
  // because "goud" is one of the atelier's real values and a rack full of unlabelled
  // gold pieces is exactly what the swatch is for.
  gold: '#c8a951',
  goud: '#c8a951',
  silver: '#bdc3c7',
  zilver: '#bdc3c7',
  bronze: '#8c6d3f',
  brons: '#8c6d3f',
};

// The map is validated at module load, so a mistyped hex can never reach the DOM as a
// style. A bad entry is dropped and its value simply gets no swatch — the same outcome
// as an unrecognised word, which is the safe direction to fail in.
const HEX = /^#[0-9a-f]{6}$/;
const SWATCHES: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(KNOWN).filter(([, hex]) => HEX.test(hex)),
);

/**
 * The hex for a colour VALUE, or `null` when we do not confidently know it.
 *
 * Matching is exact after lowercasing and collapsing whitespace — nothing else. See the
 * header for why there is no fuzzy fallback.
 */
export function swatchFor(value: string): string | null {
  const key = value.trim().toLowerCase().replace(/\s+/g, ' ');
  return SWATCHES[key] ?? null;
}

/**
 * Is this field the one that holds colours? The sheet knows its field by TITLE, and the
 * atelier's own field may be spelled either way — the interface is English, the data was
 * entered in Dutch.
 */
export function isColourField(title: string): boolean {
  const key = title.trim().toLowerCase();
  return key === 'colour' || key === 'color' || key === 'kleur';
}

/**
 * A very light or very dark swatch needs an outline or it disappears into the sheet.
 * White on white is the case that made this necessary.
 */
export function needsOutline(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Rec. 601 luma, which is plenty for "is this nearly white".
  return 0.299 * r + 0.587 * g + 0.114 * b > 200;
}
